/**
 * ComingSoon — honest placeholder for surfaces the shell routes to but a
 * later task owns (Trip → T12.1.6, Cards → T12.1.9).
 *
 * Per T12.1.5.1.1: nav destinations without their owning feature yet render
 * a clearly-labelled "coming soon" screen — not a broken or empty route.
 * Tokens-only styling (CP5): no hex, no raw px.
 */

import type { CSSProperties, ReactNode } from 'react';

const wrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-8) var(--space-4)',
  color: 'var(--color-text-muted)',
};

const iconStyle: CSSProperties = {
  fontSize: '2.5rem',
  lineHeight: 1,
};

const titleStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — placeholder page heading → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

const bodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm)',
  maxWidth: '22rem',
};

const badgeStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-primary)',
  backgroundColor: 'var(--color-bg-muted)',
  padding: 'var(--space-1) var(--space-3)',
  borderRadius: 'var(--radius-pill)',
};

export interface ComingSoonProps {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  'data-testid'?: string;
}

export function ComingSoon({
  icon,
  title,
  children,
  'data-testid': testId,
}: ComingSoonProps): JSX.Element {
  return (
    <div style={wrapStyle} data-testid={testId ?? 'coming-soon'}>
      {icon != null && (
        <div style={iconStyle} aria-hidden="true">
          {icon}
        </div>
      )}
      <h2 style={titleStyle}>{title}</h2>
      {children != null && <p style={bodyStyle}>{children}</p>}
      <span style={badgeStyle}>Coming soon</span>
    </div>
  );
}
